import React, { useEffect, useCallback, Fragment } from "react";
// https://github.com/facebook/react/issues/10780
import MapGl, { Marker, Popup, NavigationControl } from "react-map-gl";
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';
import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker.js'
/* eslint-disable-next-line */ 
// import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker'; // Load worker code separately with worker-loader
 
import { useAppContext } from "./helpers/account-context";

import { ICONS, processLocations, sortNearbyLocations } from "./helpers/helper";
import { lookupLocations, CBAnalytics } from "./helpers/apiEndpoints";
import {
  requestGeocode,
  convertJsonToParams,
  syncLocationStates,
} from "./helpers/sharedUtils";
import { GMarker, ClusterMarker, GeoMarker, InfoWindow } from "./Maps/Gmaps";
import { ListResults } from "./Maps/Results";
import LocationDialog from "./Maps/LocationDialog";
import FilterDialog from "./Maps/FilterDialog";
import SearchOuter from "./Maps/SearchOuter";
import SearchInner from "./Maps/SearchInner";
import LeadForm from "./Maps/LeadForm";
import { Loading } from "./Maps/Components/Mixin";
import TaggableFilters from "./Maps/TaggableFilters";
import { useMixMap } from "./helpers/useMixMap";
import getCrossOriginWorkerURL from 'crossoriginworker';

async function createWorker() {
  console.log('createWorker')
  const workerURL = await getCrossOriginWorkerURL('https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/2.15.0/mapbox-gl-csp-worker.min.js');
  return new Worker(workerURL);
}


mapboxgl.workerClass = MapboxWorker; // Wire up loaded worker to be used instead of the default
// mapboxgl.workerClass = require('mapbox-gl/dist/mapbox-gl-csp-worker'); // Wire up loaded worker to be used instead of the default
mapboxgl.workerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/2.15.0/mapbox-gl-csp-worker.min.js';
// mapboxgl.workerUrl = "https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/1.3.1/mapbox-gl-csp-worker.js";

const CLUSTER_MAX_ZOOM = 14;

const isInBounds = (bounds, { properties: { lat, lng } }) => {
  return (
    lng >= bounds.nw_lng &&
    lng <= bounds.se_lng &&
    lat >= bounds.nw_lat &&
    lat <= bounds.se_lat
  );
};

const detectPointsInBounds = ({ bounds }, points) => {
  if (!bounds) return false;
  return points.find((point) => isInBounds(bounds, point));
};

const appendClassName = (account, initedData) => {
  const {
    mapTheme,
    filterLayout,
    tagsLayout,
    filtersPosition,
    contentPositionDesktop,
    contentPositionMobile,
  } = initedData;
  const themePrefix = "cbmap-theme";
  return `cbmap-widget
    ${themePrefix}-${mapTheme || account.map_theme || "default"} 
    ${themePrefix}_filters-layout-${filterLayout || "insiderow"}
    ${themePrefix}_tags-layout-${tagsLayout || "dropdown"}
    ${themePrefix}_filters-position-${filtersPosition || "inside"}
    ${themePrefix}_content-position_d-${contentPositionDesktop || "left"}
    ${themePrefix}_content-position_m-${contentPositionMobile || "top"}
  `;
};

const timerFunc = (cb, seconds) => {
  if (seconds === 0) return cb();
  setTimeout(() => {
    cb();
  }, seconds * 1000);
};

class MapboxGeocoder {
  constructor(mapbox_api_key) {
    this.mapbox_api_key = mapbox_api_key;
  }
  geocode = (opts) => {
    let query = opts.address || opts.place_id;
    if (opts.location) {
      query = `${opts.location.lng},${opts.location.lat}`;
    }
    const params = convertJsonToParams({
      access_token: this.mapbox_api_key,
    });
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?${params}`;
    return new Promise((resolve, reject) => {
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          const predictions = data.features.map((item) => ({
            ...item,
            place_id: item.id,
            description: item.place_name,
            isMapbox: true,
            formatted_address: item.place_name,
            geometry: {
              ...item.geometry,
              location: {
                lng: item.geometry.coordinates[0],
                lat: item.geometry.coordinates[1],
              },
            },
          }));
          resolve({ results: predictions });
        })
        .catch(reject);
    });
  };
  getPlacePredictions = (req, cb) => {
    const params = convertJsonToParams({
      country: req.componentRestrictions
        ? req.componentRestrictions.country
        : "",
      access_token: this.mapbox_api_key,
    });
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${req.input}.json?${params}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        const predictions = data.features.map((item) => ({
          ...item,
          place_id: item.id,
          description: item.place_name,
          isMapbox: true,
        }));

        cb(predictions);
      });
  };
}

const MapBox = (props) => {
  const {
    account,
    initedData = {},
    currentGeo,
    setCurrentGeo,
    getLocale,
  } = useAppContext();
  const {
    clusterEnabled,
    clusterSettings,
    defaultCenter,
    currentParams,
    containerRef,
    contentRef,
    mapViewRef,
    mapRef,
    geocoder,
    setGeocoder,
    filters,
    setFilters,
    isLoading,
    setIsLoading,
    isCollapsed,
    setIsCollapsed,
    openingFilters,
    setOpeningFilters,
    mapViewPort,
    setMapViewPort,
    selectedLocation,
    setSelectedLocation,
    hoveredLocation,
    setHoveredLocation,
    points,
    setPoints,
    clusters,
    supercluster,
  } = useMixMap({});


  const { geo_type, enable_custom_params, mapbox_api_key } = account;

  const zoomAndMoveMap = useCallback(
    (opts) => {
      timerFunc(
        () => {
          mapRef.current.easeTo({
            center: [opts.lng, opts.lat],
            zoom: opts.zoom || mapViewPort.zoom,
            duration: 500,
          });
        },
        opts.zoom ? 0.2 : 0
      );
    },
    [mapViewPort.zoom]
  );

  const forceToCenter = useCallback(
    (opts = {}) => {
      if (mapRef && mapRef.current && opts.lat && opts.lng) {
        zoomAndMoveMap(opts);
      } else {
        timerFunc(() => {
          if (mapRef.current) {
            zoomAndMoveMap(opts);
          }
        }, 2);
      }
    },
    [zoomAndMoveMap]
  );

  const doFilters = useCallback(
    async (params, cb = null) => {
      setIsLoading(true);
      lookupLocations(account.uid, params)
        .then(({ resources }) => {
          const newPoints = processLocations(resources);
          console.log("doFilters", params);
          if (params && params.geo && params.geo.lat && params.geo.lng) {      
            setPoints(sortNearbyLocations(newPoints, params.geo.lat, params.geo.lng, true));
          } else {
            setPoints(newPoints);          
          }

          if (!filters.isFiltered) {
            setFilters({ ...filters, ...params, isFiltered: 1 });
          }
          setIsLoading(false);
          const isInside = detectPointsInBounds(mapViewPort, newPoints);
          if (!isInside && newPoints.length > 0) {
            forceToCenter({
              lat: +newPoints[0].properties.lat,
              lng: +newPoints[0].properties.lng,
            });
          } else {
            forceToCenter({
              lat: +currentGeo.lat,
              lng: +currentGeo.lng,
            });
          }
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.resize();
            }
          }, 200);
          if (cb) cb(resources);
        })
        .catch((err) => {
          console.log("error", err);
          setIsLoading(false);
        });
    },
    [account, mapViewPort, filters, forceToCenter]
  );
  const setWorker = async () => {
    // window.xmapboxgl = mapboxgl;
    // window.createWorker = createWorker;
    console.log('setWorker');
    // mapboxgl.workerClass = await createWorker(); // Wire up loaded worker to be used instead of the default
    // window.mapboxgl = {...mapboxgl, workerClass: await createWorker()};
  }
  useEffect(() => {
    setWorker();
    const defaultParams = {};
    let initFiltering = true;
    if (!!initedData.selectedLocation) {
      defaultParams.location = initedData.selectedLocation;
    }
    if (!!currentParams.location) {
      defaultParams.location = currentParams.location;
    } else if (geo_type === "geo_browser_ip") {
      defaultParams.geo = filters.geo;
    } else if (geo_type === "disable") {
      defaultParams.all = 1;
    } else if (geo_type === "typing") {
      initFiltering = false;
    }
    if (!!initedData.selectedLocation || initFiltering) {
      doFilters({ ...defaultParams }, (resources) => {
        const loc = resources.find(
          (loc) =>
            loc.uid === currentParams.location ||
            loc.slug === currentParams.location
        );
        if (loc) {
          setFilters({ ...filters, isFiltered: true, search: loc.name });
        } else if (geo_type === "geo_browser_ip") {
          setFilters({ ...filters, isFiltered: true, search: currentGeo.address });
        }
      });
    }

  }, []);

  const initGeoData = async (newGeocoder) => {
    if (!currentParams.search || currentParams.search.length < 3) {
      if (geo_type === "geo_browser_ip" && !currentGeo.address) {
        const currentLocation = await requestGeocode(newGeocoder, {
          location: { lat: currentGeo.lat, lng: currentGeo.lng },
        });
        console.log("currentLocation", currentLocation);
        if (currentLocation.results.length > 0) {
          setFilters({
            ...filters,
            search: currentLocation.results[0].place_name,
          });
          setCurrentGeo({
            ...currentGeo,
            address: currentLocation.results[0].place_name,
          });
        }
      }
      return null;
    }
    console.log("initGeoData", currentParams);

    const { results } = await requestGeocode(newGeocoder, {
      address: currentParams.search,
    });

    if (!results || results.length === 0) return null;
    setFilters({ ...filters, search: currentParams.search });

    const searchedLoc = results[0].geometry.location;
    const { lat, lng } = searchedLoc;
    const geo =
      typeof lat === "function" ? { lat: lat(), lng: lng() } : { lat, lng };

    const newFilters = {
      ...filters,
      geo: geo,
      search: currentParams.search,
    };

    console.log("requestGeocode", results, currentParams, newFilters);

    setFilters({
      ...newFilters,
      isFiltered: true,
    });

    doFilters(newFilters);
    forceToCenter({
      ...geo,
      zoom: 17,
    });
  };

  const handleSelectLocation = useCallback(
    (place) => {
      if (!place) return setSelectedLocation(null);
      console.log("handleSelectLocation", place);
      setSelectedLocation(place);

      forceToCenter({
        lng: place.properties.lng,
        lat: place.properties.lat,
        zoom: Math.max(mapViewPort.zoom, CLUSTER_MAX_ZOOM + 1),
      });

      const { search, ...newParams } = currentParams;
      if (enable_custom_params) {
        syncLocationStates({
          ...newParams,
          location: place.properties.slug,
        });
      }
      if (!initedData.preview) {
        CBAnalytics.click(account.uid, {
          location_name: place.properties.name,
          address: place.properties.full_address,
          location_id: place.properties.num,
          clicked_url: window.location.href,
        });
      }
    },
    [forceToCenter, account.uid, currentParams]
  );

  const handleHoverLocation = (place) => {
    setHoveredLocation(place);
  };

  const handleClickCluster = (cluster) => {
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(cluster.id),
      CLUSTER_MAX_ZOOM + 1
    );

    mapRef.current.easeTo({
      center: cluster.geometry.coordinates,
      zoom: expansionZoom,
      duration: 500,
    });
    setMapViewPort({ ...mapViewPort, zoom: expansionZoom });
  };

  const handleOpenFilters = (filterType) => {
    setOpeningFilters(filterType);
  };

  const handleChangeFilters = (ft) => {
    setFilters({ ...ft });
  };

  const handleChangeMapBounds = useCallback(
    (newViewPort) => {
      const aBounds = mapRef.current.getBounds();
      const newBounds = {
        nw_lng: aBounds._sw.lng,
        se_lat: aBounds._sw.lat,
        se_lng: aBounds._ne.lng,
        nw_lat: aBounds._ne.lat,
      };

      setMapViewPort({
        ...mapViewPort,
        zoom: newViewPort.viewState.zoom,
        bounds: newBounds,
      });

      setFilters({
        ...filters,
        bounds: { ...newBounds },
      });
    },
    [filters, mapViewPort]
  );

  const handleSelectSearch = (data) => {
    const { lat, lng, location, ...existingFilters } = filters;
    const newFilters = {
      ...existingFilters,
      search: data.search,
      geo: {
        lat: data.lat ? data.lat : "",
        lng: data.lng ? data.lng : "",
      },
    };
    if(data.search && data.search.length > 0) {
      delete newFilters.country
      delete newFilters.province
      delete newFilters.city
      delete newFilters.all
    }
    if (data.location) {
      newFilters.location = data.location;
    }

    setFilters({ ...newFilters });
    doFilters({ ...newFilters });
    if (enable_custom_params && !data.lat && !data.lng) {
      const { location, search, ...newParams } = currentParams;
      syncLocationStates({ ...newParams });
    }
  };

  const handleApplyFilters = useCallback(
    (ft) => {
      if (ft) {
        const newFilters = {...filters, ...ft};
        if (ft && ft.all === 1) {
          delete newFilters.search; 
          delete newFilters.geo; 
          delete newFilters.location; 
        }  
        setFilters({ ...newFilters });
        doFilters({ ...newFilters });
      } else {
        doFilters({ ...filters });
      }
      setOpeningFilters(false);
      const { location, ...newParams } = currentParams;
      if (enable_custom_params) {
        syncLocationStates({ ...newParams });
      }
    },
    [currentParams, filters, enable_custom_params, doFilters]
  );
  useEffect(() => {
    if (mapRef.current) {
      const aBounds = mapRef.current.getBounds();
      const newBounds = {
        nw_lng: aBounds._sw.lng,
        se_lat: aBounds._sw.lat,
        se_lng: aBounds._ne.lng,
        nw_lat: aBounds._ne.lat,
      };
      setMapViewPort({
        ...mapViewPort,
        bounds: newBounds,
      });
      const mapboxCoder = new MapboxGeocoder(mapbox_api_key);
      setGeocoder(mapboxCoder);
      initGeoData(mapboxCoder);
      mapRef.current.resize();
    }
  }, [mapRef.current]);

  const handleToggleContent = (e) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.resize();
      }, 200);
    }
  };

  const dialog = account.dialog || {};
  const markers = true || clusterEnabled ? clusters : points;
  const showingDetails = dialog.detail_enabled && selectedLocation;
  const showListing = !showingDetails && !openingFilters;
  const isMobile = window.screen.availWidth < 767;
  const mapHeight = isMobile ? 700 : containerRef.current?.clientHeight;
  const maxContentHeight = mapHeight - mapViewRef.current?.clientHeight;
  const defaultStyle =
    account.mapbox_style_slug ||
    "mapbox://styles/mapbox-map-design/cl4whef7m000714pc44f3qaxs";

  return (
    <div className={appendClassName(account, initedData)}>
      <SearchOuter
        filters={filters}
        onChangeSetting={handleOpenFilters}
        onSelectSearch={handleSelectSearch}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
        geocoder={geocoder}
        autoSuggestion={geocoder}
      />
      <TaggableFilters
        filters={filters}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
      />

      <div
        className="cbmap-wrapper"
        ref={containerRef}
        style={{
          height: !isMobile && initedData.widgetHeight ? initedData.widgetHeight : "",
        }}
      >
        <div
          className={`cbmap-content ${isCollapsed ? "is-collapsed" : ""}`}
          ref={contentRef}
          style={{
            height:
              isMobile && (isLoading || openingFilters) ? maxContentHeight : "",
          }}
        >
          <div
            className="cbmap-content-togglebar"
            onClick={handleToggleContent}
            dangerouslySetInnerHTML={{ __html: ICONS.CARET }}
            title={isCollapsed ? getLocale("show") : getLocale("hide")}
          />
          <Loading isLoading={isLoading} />
          <FilterDialog
            isOpen={!isLoading && openingFilters}
            filters={filters}
            filterType={openingFilters}
            onChangeFilters={handleChangeFilters}
            onApplyFilters={handleApplyFilters}
            onClose={(e) => setOpeningFilters(false)}
          />
          {showingDetails && (
            <LocationDialog
              isOpen={!!selectedLocation}
              item={selectedLocation}
              onClose={(e) => setSelectedLocation(null)}
            />
          )}

          <SearchInner
            isOpen={!isLoading && showListing}
            filters={filters}
            onChangeFilters={handleChangeFilters}
            onChangeSetting={handleOpenFilters}
            onSelectSearch={handleSelectSearch}
            onApplyFilters={handleApplyFilters}
            geocoder={geocoder}
            autoSuggestion={geocoder}
          />
          {!isLoading && !showingDetails && !openingFilters && (
            <LeadForm
              filters={filters}
              results={points}
              onApplyFilters={handleApplyFilters}
            />
          )}

          <ListResults
            showListing={!isLoading && showListing}
            selectedLocation={selectedLocation}
            hoveredLocation={hoveredLocation}
            handleSelectLocation={handleSelectLocation}
            handleHoverLocation={handleHoverLocation}
            points={points}
            currentGeo={currentGeo}
          />
        </div>
        <div
          className="cbmap-mapview"
          ref={mapViewRef}
          style={{ height: mapHeight }}
        >
          <MapGl
            ref={mapRef}
            initialViewState={{
              latitude: defaultCenter.lat,
              longitude: defaultCenter.lng,
              zoom: 8,
            }}
            // workerClass={MapboxWorker}
            // workerUrl="https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/2.15.0/mapbox-gl-csp-worker.min.js"
            // workerUrl="https://8e52-2a09-bac5-d468-16dc-00-247-19.ngrok-free.app/mapbox-gl-csp-worker.js"
            onMove={handleChangeMapBounds}            
            mapboxAccessToken={mapbox_api_key}
            mapStyle={defaultStyle}
          >
            {currentGeo && currentGeo.lat && currentGeo.lng && (
              <Marker
                id="current-geo"
                latitude={currentGeo.lat}
                longitude={currentGeo.lng}
              >
                <GeoMarker
                  latitude={currentGeo.lat}
                  longitude={currentGeo.lng}
                />
              </Marker>
            )}
            {markers.map((cluster) => {
              const {
                properties: { cluster: isCluster },
                geometry: {
                  coordinates: [longitude, latitude],
                },
              } = cluster;

              if (isCluster) {
                return (
                  <Marker
                    key={`clusterx-${cluster.id}`}
                    latitude={latitude}
                    longitude={longitude}
                    onClick={() => handleClickCluster(cluster)}
                  >
                    <ClusterMarker
                      settings={clusterSettings}
                      cluster={cluster}
                      points={points}
                    />
                  </Marker>
                );
              }

              return (
                <Fragment key={`pin-${cluster.properties.uid}`}>
                  <Marker
                    key={`kmarker-${cluster.properties.uid}`}
                    latitude={latitude}
                    longitude={longitude}
                    anchor="bottom"
                    // onClick={(e) =>handleSelectLocation(cluster)}
                  >
                    <GMarker
                      key={`marker-${cluster.properties.uid}`}
                      place={cluster}
                      currentGeo={currentGeo}
                      selectedLocation={selectedLocation}
                      hoveredLocation={hoveredLocation}
                      onClose={(e) => console.log(e)}
                      onSelectLocation={(e) => handleSelectLocation(cluster)}
                      onHoverLocation={(e) => handleHoverLocation(cluster)}
                      hideInfo={true}
                    />
                  </Marker>
                </Fragment>
              );
            })}

            {(selectedLocation ||
              (clusterSettings.marker_hovering && hoveredLocation)) && (
              <Popup
                longitude={
                  selectedLocation
                    ? selectedLocation.properties.lng
                    : hoveredLocation.properties?.lng
                }
                latitude={
                  selectedLocation
                    ? selectedLocation.properties.lat
                    : hoveredLocation.properties?.lat
                }
                anchor="bottom"
                maxWidth="450px"
                onClose={() => {
                  handleHoverLocation(null);
                }}
              >
                <InfoWindow
                  onHoverLocation={(e) =>
                    handleHoverLocation(
                      selectedLocation ? selectedLocation : hoveredLocation
                    )
                  }
                  selectedLocation={selectedLocation}
                  hoveredLocation={hoveredLocation}
                  item={selectedLocation ? selectedLocation : hoveredLocation}
                  onClose={() => console.log("info close")}
                />
              </Popup>
            )}
            <NavigationControl />
          </MapGl>
        </div>
      </div>
    </div>
  );
};

export default MapBox;
